<template>
    <base-modal size="lg" hide-footer hide-header @close="close">
        <div class="col-12" @click.stop="">
            <h3 class="pl-1 pt-2">{{ $t('danger_report.title') }}</h3>

            <!-- Location button -->
            <div class="col-12 nopads mb-2 mt-2 d-flex justify-content-between">
                <button
                    class="btn btn-sm btn-primary form-button"
                    @click.stop="showDrawGeometryWizard = !showDrawGeometryWizard"
                >
                    {{ report.location ? $t('map.redefine_geometry') : $t('map.define_geometry') }}
                </button>

                <google-street-view-link v-if="report.location" :lat-lon="getMapPoint()" />
            </div>

            <div>
                <marker-editor-map
                    v-if="report.location"
                    :id="report.id"
                    class="item-map"
                    :lat="report.location.y"
                    :lon="report.location.x"
                    :type="OBSERVATION"
                    :draggable="false"
                />
                <div class="col-sm-12">
                    <div class="stat-container nopads" style="overflow: hidden">
                        <Flicking ref="flicking" :options="options" :plugins="plugins" class="pointer nopads text-center"
                                  style="height: 15em;"
                                  v-if="report.photos && report.photos.length > 0"
                        >
                            <template
                                v-for="photo in report.photos"
                                :key="photo.id"
                            >
                                <ImageView
                                    ref="image"
                                    class="flicking-panel py-2"
                                    :contain="true"
                                    :url="getUrlForPhoto(photo)"
                                    @click="openImage"
                                    :controlSize="`sm`"
                                />
                            </template>
                            <template #viewport>
                                <div class="flicking-pagination flicking-pagination-bullets-container" />
                            </template>
                        </Flicking>
                        <div v-else
                             style="padding-top: 6em; height: 15em; text-align: center">
                            {{ $t('power_stations.no_photos') }}
                        </div>
                    </div>
                </div>

                <!-- Details -->
                <div class="row">
                    <div class="col-sm-6 nopads">
                        <span class="item-detail-title">{{ $t('danger_report.report_id') }}</span>
                        <span class="item-detail-text">{{ report.id }}</span>
                    </div>
                    <div class="col-sm-6 nopads">
                        <span class="item-detail-title">{{ $t('danger_report.reporter') }}</span>
                        <span class="item-detail-text">{{ report.reporter.last_name }}, {{
                                report.reporter.first_name
                            }}</span>
                    </div>
                </div>

                <div class="row">
                    <div class="col-sm-6 nopads">
                        <span class="item-detail-title">{{ $t('danger_report.event_time') }}</span>
                        <span class="item-detail-text">{{ toLocalTime(report.event_time) }}</span>
                    </div>
                    <div class="col-sm-6 nopads">
                        <span class="item-detail-title">{{ $t('danger_report.event_type') }}</span>
                        <span class="item-detail-text">{{ getDangerTypeName(report.event_type) }}</span>
                    </div>
                </div>

                <div class="row">
                    <div class="col-sm-6 nopads">
                        <span class="item-detail-title">{{ $t('danger_report.reporting_company') }}</span>
                        <span class="item-detail-text">{{ report.reporting_company.name }}</span>
                    </div>
                    <div class="col-sm-6 nopads">
                        <span class="item-detail-title">{{ $t('danger_report.contract') }}</span>
                        <span
                            v-if="report.contract"
                            class="item-detail-text"
                        >
                            {{ report.contract.name }}
                        </span>
                        <span
                            v-else
                            class="item-detail-text"
                        >
                        -
                        </span>
                    </div>
                </div>

                <div class="row">
                    <div class="col-sm-6 nopads">
                        <span class="item-detail-title">{{ $t('danger_report.description') }}</span>
                        <span class="item-detail-text">{{ report.description ? report.description : '-' }}</span>
                    </div>
                </div>

                <div class="row">
                    <div class="col-sm-6 nopads">
                        <span class="item-detail-title">{{ $t('danger_report.description_conditions') }}</span>
                        <span class="item-detail-text">{{ report.description_conditions ? report.description_conditions : '-' }}</span>
                    </div>
                    <div class="col-sm-6 nopads">
                        <span class="item-detail-title">{{ $t('danger_report.proposal_for_preventive_actions') }}</span>
                        <span class="item-detail-text">{{ report.proposal_for_preventive_actions ? report.proposal_for_preventive_actions : '-' }}</span>
                    </div>
                </div>

                <div class="row">
                    <div class="col-sm-6 nopads">
                        <span class="item-detail-title">{{ $t('danger_report.consequences.title') }}</span>
                        <span class="item-detail-text">{{ report.consequences ? getDangerConsequencesName(report.consequences) : '-' }}</span>
                    </div>
                    <div class="col-sm-6 nopads">
                        <span class="item-detail-title">{{ $t('danger_report.consequences.info') }}</span>
                        <span class="item-detail-text">{{ report.consequences_info ? report.consequences_info : '-' }}</span>
                    </div>
                </div>

                <div class="row">
                    <div class="col-sm-12 nopads">
                        <span class="item-detail-title">{{ $t('danger_report.informing') }}</span>
                        <span class="item-detail-text">{{ report.informing }}</span>
                    </div>
                </div>

                <div class="row" v-if="report.handled">
                    <div class="col-sm-6 nopads">
                        <span class="item-detail-title">{{ $t('danger_report.handled_by') }}</span>
                        <span class="item-detail-text">{{
                                report.handled_by.last_name
                            }}, {{ report.handled_by.first_name }}</span>
                    </div>
                    <div class="col-sm-6 nopads">
                        <span class="item-detail-title">{{ $t('danger_report.note') }}</span>
                        <span class="item-detail-text">{{ report.handling_note }}</span>
                    </div>
                </div>


                <!-- Danger photo -->
                <div v-if="report">
                    <!-- Show selected photos -->
                    <div
                        v-for="(file, index) in photos"
                        :key="index"
                        class="d-flex align-items-center justify-content-between mb-3"
                    >
                        <span>{{ file?.name || file }}</span>
                        <button
                            class="btn btn-outline-danger btn-sm"
                            type="button"
                            @click="removePhoto(index)"
                        >
                            {{ $t('common.remove') }}
                        </button>
                    </div>

                    <!-- Hidden file input -->
                    <input
                        ref="photoInput"
                        type="file"
                        accept="image/jpeg,image/jpg,image/png"
                        class="visually-hidden"
                        @change="handlePhotoChange"
                    />

                    <!-- Custom "Add photo" button -->
                    <div class="col-12 d-flex justify-content-end">
                        <button
                            class="btn btn-outline-primary btn-sm"
                            type="button"
                            @click="triggerPhotoInput"
                        >
                            {{ $t('power_stations.add_photo') }}
                        </button>
                    </div>
                </div>
                <hr/>
                <!-- danger report comments -->
                <comment-container
                    class="px-2"
                    :topic="commentTopics.TOPIC_DANGER_REPORTS"
                    :id="report.id"
                    :user="user"
                />
                <hr/>
                <div class="col-12 d-flex justify-content-end">
                    <button
                        class="btn btn-secondary form-button"
                        @click.stop="close"
                    >
                        {{ $t('common.close') }}
                    </button>
                    <button
                        class="btn btn-primary form-button"
                        @click.stop="handleReportModal=true"
                        :disabled="loading || !isObserver || report.handled !== false"
                    >
                        {{ $t('danger_report.handle') }}
                    </button>
                    <button
                        class="btn btn-success form-button"
                        :disabled="getValidAttachments(photos).length === 0||loading"
                        @click.stop="addPhotos"
                    >
                        {{ $t('common.save') }}
                    </button>
                </div>
            </div>

            <base-modal
                v-if="handleReportModal"
                :title="$t('work_permission.handle_request')"
                :prevent-close="true"
                hide-footer
            >
                <span>{{ $t('work_permission.handlers_note') }}</span>
                <textarea
                    id="info"
                    v-model="handlingNote"
                    class="form-control"
                    rows="3"
                    style="resize: vertical; max-height: calc(1.5em * 6);"
                ></textarea>
                <div class="button-container top-margin">
                    <button
                        class="btn btn-secondary form-button"
                        @click.stop="handleReportModal=false">
                        {{ $t('common.close') }}
                    </button>
                    <button
                        size="lg"
                        class="btn btn-success form-button"
                        @click.stop="handleReport"
                        :disabled="handlingNote===null||handlingNote.trim().length===0">
                        {{ $t('common.save') }}
                    </button>
                </div>
            </base-modal>

            <draw-geometry-wizard
                v-if="showDrawGeometryWizard !== false"
                :saved-geometry="savedGeometryForWizard"
                :observation-mode="true"
                :type="OTHER"
                @close="closeDrawGeometryWizard"
                @add-drawn-geometry="addGeometryToDangerReport"
            />
        </div>
        <!-- Fullscreen photo -->
        <Transition name="fade">
            <fullscreen-image
                v-if="fullScreenPhotoData"
                :largeImages="fullScreenPhotoData"
                :panelIndex="panelIndex"
                @close="closePhoto"/>
        </Transition>
        <div
            v-if="loading"
            id="loader"
            class="spinner"
        />
    </base-modal>
</template>

<script>
import {timeUtils} from '../mixins/TimeUtils'
import ImageView from '../view/ImageView'
import {restApi} from '../mixins/RestApiMixin'
import MarkerEditorMap from '../map/MarkerEditorMap'
import {mapHelper} from '../mixins/MapMixin'
import {dangerHelper} from "../mixins/DangerMixin";
import {attachmentHelper} from '../mixins/AttachmentMixin'
import FullscreenImage from "@/components/view/FullscreenImage";
import {Pagination} from "@egjs/flicking-plugins";
import "@egjs/flicking-plugins/dist/pagination.css";
import "@egjs/vue3-flicking/dist/flicking.css";
import Flicking from "@egjs/vue3-flicking";
import CommentContainer from "@/components/comments/CommentContainer.vue";
import {commentTopics} from "@/components/mixins/CommentMixin";
import BaseModal from "@/components/BaseModal";
import DrawGeometryWizard from "@/components/shapeimport/DrawGeometryWizard.vue";
import GoogleStreetViewLink from "@/components/utils/GoogleStreetViewLink.vue";

export default {
    name: 'DangerReportDetails',
    components: {
        GoogleStreetViewLink,
        DrawGeometryWizard, BaseModal, CommentContainer, FullscreenImage, MarkerEditorMap, ImageView, Flicking},
    mixins: [timeUtils, restApi, mapHelper, dangerHelper, attachmentHelper],
    props: {
        report: {
            type: Object,
            default: null
        },
        user: {
            type: Object,
            default: null
        },
        isObserver: Boolean
    },
    data() {
        return {
            map: null,
            photos: [],
            loading: false,
            handlingNote: null,
            handleReportModal: false,
            fullScreenPhotoData: null,
            plugins: [new Pagination({ type: 'bullet' })],
            options: {
                preventDefaultOnDrag: true,
                circular: true,
            },
            panelIndex: 0,
            showDrawGeometryWizard: false,
            reportLocation: null,
        }
    },
    computed: {
        commentTopics() {
            return commentTopics
        },
        savedGeometryForWizard() {
            if (!this.report.location) {
                return {
                    // use users company location as fallback if the danger report does not have the location set.
                    // this allows the user to give location for the report
                    point: {
                        y: this.user.company.location.y,
                        x: this.user.company.location.x
                    }
                }
            }

            return {
                point: {
                    y: this.report.location.y,
                    x: this.report.location.x
                }
            };
        }
    },
    methods: {
        addGeometryToDangerReport(geometry) {
            if (geometry.point && geometry.point.lat && geometry.point.lon) {
                // Transform DrawGeometryWizard response back to danger report format
                this.report.location = {
                    y: geometry.point.lat,
                    x: geometry.point.lon
                };

                this.saveDangerReportLocation();
            }
        },
        // get map point for google street view component
        getMapPoint() {
            if (!this.report.location) {
                return null;
            }

            return {
                lat: this.report.location.y,
                lon: this.report.location.x
            }
        },

        closeDrawGeometryWizard() {
            this.showDrawGeometryWizard = false
        },
        saveDangerReportLocation() {
            if (!this.report) return;

            const url = `${this.dangerReportUrl}/${this.report.id}/location`;
            const updatedLocation = {
                point: this.report.location
            };

            this.restUpdate(url, updatedLocation, this.locationSaveSuccess, this.locationSaveFail);
        },

        locationSaveSuccess() {
            this.closeDrawGeometryWizard()
        },

        locationSaveFail() {
            this.loading = false;
            this.showAlert(this.$t('common.alert_update_failed'));
        },
        triggerPhotoInput() {
            this.$refs.photoInput?.click();
        },
        handlePhotoChange(event) {
            const file = event.target.files[0];
            if (file) {
                this.photos.push(file);
            }
            // Clear input value so the same file can be selected again if needed
            event.target.value = '';
        },
        removePhoto(index) {
            this.photos.splice(index, 1);
        },

        close: function () {
            this.$emit('close')
        },

        addPhotos: function () {
            var photos = this.getValidAttachments(this.photos)
            if (photos.length > 0 && this.report) {
                this.uploadPhoto(photos)
            } else {
                this.fail()
            }
        },

        uploadPhoto: function (photos) {
            if (photos.length > 0) {
                this.loading = true
                let formData = new FormData()
                photos.forEach(photoFile => {
                    if (photoFile !== undefined && photoFile !== null) {
                        formData.append('photos[]', photoFile)
                    }
                })
                let url = this.dangerReportUrl + "/" + this.report.id + "/photo"
                this.restPostWithFile(url, {}, formData, this.photoSuccess, this.fail)
            }
        },

        photoSuccess: function () {
            this.loading = false
            this.$emit('closeAndUpdate')
        },

        fail: function () {
            this.loading = false
            this.showAlert(this.$t('common.alert_update_failed'))
        },

        getUrlForPhoto(photo) {
            return this.dangerReportUrl + '/' + this.report.id + '/photo/' + photo.id
        },

        handleReport: function () {
            this.loading = true
            let url = this.dangerReportUrl + "/" + this.report.id + "/handle"
            this.restUpdate(url, {note: this.handlingNote}, this.handleReportSuccess, this.fail)
        },

        handleReportSuccess: function () {
            this.loading = true
            this.$emit('closeAndUpdate')
        },

        openImage: function () {
            const flicking = this.$refs.flicking
            const imageData = this.$refs.image
            this.fullScreenPhotoData = []
            imageData.forEach(src => {
                this.fullScreenPhotoData.push(src)
            })
            this.panelIndex = flicking.index

        },

        closePhoto() {
            this.fullScreenPhotoData = undefined
        }

    }
}
</script>

<style scoped>
.map-container {
    height: 30em;
    position: relative;
}
</style>
